import { useMemo, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentLeadTime,
  AppointmentMetricType,
  TimeFilterType,
  AppointmentConfirmationStatus,
  AtRiskStatus,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import {
  TooltipHeader,
  AnalyticsInfoPopover,
  X_AXIS_TIME_FILTER_OPTIONS,
  formatDailyXAxisTick,
  formatTooltipTitle,
} from '@frontend/analytics-shared';
import { BarClick, Chart, ChartEditPanel } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useBarClickRedirection } from '../../hooks/use-bar-click-redirection';
import { APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS } from '../../utils/appointment-lead-time-options';
import { APPOINTMENT_NO_SHOW_RISK_Y_AXIS_GROUPS } from '../../utils/appointment-no-show-risk-options';
import { createBarCountTooltipData, getLegendCountsFromSummary } from '../../utils/appointment-tooltip-utils';
import { buildChartSubtitle } from '../../utils/build-chart-subtitle';

const colors = {
  confirmed: theme.colors.primary20,
  unconfirmed: theme.colors.warning10,
};
const strokeColors = {
  confirmed: theme.colors.primary50,
  unconfirmed: theme.colorsV2.yellow50(),
};

// Darker shades used for the count text in the tooltip so it stays legible
// against the lighter bar fill colors above.
const labelColors: Record<string, string> = {
  confirmed: theme.colors.primary50,
  unconfirmed: theme.colorsV2.yellow50(),
};

const labels = {
  confirmed: 'Confirmed',
  unconfirmed: 'Unconfirmed',
};

type ConfirmationChartYAxes = {
  leadTime: AppointmentLeadTime[];
  atRiskStatus: AtRiskStatus[];
};

const defaultYAxisSelected: ConfirmationChartYAxes = {
  leadTime: [],
  atRiskStatus: [],
};
const yAxisGroups = [...APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS, ...APPOINTMENT_NO_SHOW_RISK_Y_AXIS_GROUPS];
export const allYAxisOptions = yAxisGroups.flatMap((group) => group.options);

export const AppointmentConfirmationChart = () => {
  const { t } = useTranslation('analytics');
  const { filtersInitialized, filters, filterHintText } = useAppointmentAnalyticsV2ShallowStore(
    'filtersInitialized',
    'filters',
    'filterHintText'
  );
  const handleBarClickRedirect = useBarClickRedirection();
  const [selectedXAxis, setSelectedXAxis] = useState<TimeFilterType>(TimeFilterType.DAILY);
  const [selectedYAxes, setSelectedYAxes] = useState<Record<string, string[]>>(defaultYAxisSelected);

  const currentFilters = filters[APPOINTMENT_TABS.UPCOMING];

  const { data: appointmentsData, isLoading } = AnalyticsGatewayV1.Queries.useGetAppointmentsDataQuery(
    {
      ...currentFilters,
      metricType: AppointmentMetricType.APPOINTMENT_CONFIRMATION,
      filterType: selectedXAxis,
      leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
      atRiskStatus:
        selectedYAxes.atRiskStatus.length > 0
          ? ([...selectedYAxes.atRiskStatus, AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH] as AtRiskStatus[])
          : undefined,
    },
    {
      enabled: filtersInitialized && (currentFilters?.locationId?.length ?? 0) > 0,
    }
  );

  // Chart Data mapped from API
  const chartData = useMemo(() => {
    if (!appointmentsData?.data) {
      return { groups: [], stackKeys: [] };
    }

    const groups = appointmentsData.data.map((item) => {
      const rawName = item.name ?? '';
      let name = rawName;
      if (selectedXAxis === TimeFilterType.HOURLY) {
        const hour = Number(name);
        if (!isNaN(hour)) {
          const h = hour % 12 || 12;
          name = `${h} ${hour < 12 ? 'am' : 'pm'}`;
        }
      } else if (selectedXAxis === TimeFilterType.DAY_OF_WEEK) {
        name = t(`${name}s`, { ns: 'analytics' });
      } else if (selectedXAxis === TimeFilterType.DAILY) {
        name = formatDailyXAxisTick(name);
      }

      return {
        name,
        rawName,
        values: {
          confirmed: Number(item.confirmed || 0),
          unconfirmed: Number(item.unconfirmed || 0),
        },
      };
    });

    return {
      groups,
      stackKeys: ['confirmed', 'unconfirmed'],
    };
  }, [appointmentsData, selectedXAxis, t]);

  const tooltipTitlesByName = useMemo(() => {
    return (
      chartData?.groups?.reduce<Record<string, ReactNode>>((acc, group) => {
        const rawName = group.rawName;

        acc[group.name] = formatTooltipTitle(rawName, selectedXAxis);
        return acc;
      }, {}) ?? {}
    );
  }, [chartData.groups, selectedXAxis]);

  const tooltipHeadersByName = useMemo(() => {
    return chartData.groups.reduce<Record<string, JSX.Element>>((acc, group) => {
      const total = Object.values(group.values).reduce((sum, val) => sum + val, 0);
      const tooltipTitle = tooltipTitlesByName[group.name] ?? group.name;

      let finalFilterHintText;
      if (selectedXAxis === TimeFilterType.DAILY) {
        finalFilterHintText = dayjs(group.rawName).format('dddd');
      }

      acc[group.name] = (
        <TooltipHeader tooltipTitle={tooltipTitle} filterHintText={finalFilterHintText} total={total} />
      );

      return acc;
    }, {});
  }, [chartData.groups, selectedXAxis, tooltipTitlesByName]);

  // Renders the count value for each stack key in a darker label color instead
  // of the lighter bar fill color used by the default tooltip.
  const customTooltipData = useMemo(
    () =>
      createBarCountTooltipData({
        groups: chartData.groups,
        stackKeys: chartData.stackKeys,
        labelColors,
        labels,
      }),
    [chartData.groups, chartData.stackKeys]
  );

  // Count shown beside each legend label, sourced from the API `summary`.
  const legendValues = useMemo(
    () => getLegendCountsFromSummary(appointmentsData?.summary),
    [appointmentsData?.summary]
  );

  const onXChange = (xAxis: string) => {
    setSelectedXAxis(xAxis as TimeFilterType);
  };

  const onYChange = (yAxes: Record<string, string[]>) => {
    setSelectedYAxes(yAxes);
  };

  const handleOnBarClick = (args: BarClick) => {
    const confirmationStatus =
      args.barId === 'confirmed'
        ? AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED
        : args.barId === 'unconfirmed'
        ? AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED
        : undefined;

    handleBarClickRedirect(args, chartData.groups, {
      confirmationStatus,
      leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
      atRiskStatus:
        selectedYAxes.atRiskStatus.length > 0
          ? ([...selectedYAxes.atRiskStatus, AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH] as AtRiskStatus[])
          : undefined,
    });
  };
  const subtitle = useMemo(
    () =>
      buildChartSubtitle({
        selectedYAxes,
        yAxisOptions: allYAxisOptions,
        suffixMap: { atRiskStatus: 'risk status', leadTime: 'lead time' },
        filterHintText,
        t,
      }),
    [t, selectedYAxes, filterHintText]
  );

  const chartTitle = t('Appointments by confirmation status');

  return (
    <Chart
      colors={colors}
      strokeColors={strokeColors}
      labels={labels}
      style={{ marginBottom: theme.spacing(0) }}
      isLoading={isLoading}
      css={{
        '.chart-content': {
          padding: theme.spacing(2),
        },
      }}
    >
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title
            infoTip={
              <AnalyticsInfoPopover
                title={chartTitle}
                description={t(
                  'Track how many appointments scheduled in the next 7 days have been confirmed and identify those that may need follow-up.'
                )}
                popoverCss={css`
                  max-width: 393px;
                `}
              />
            }
          >
            <Text size='large'>{chartTitle}</Text>
          </Chart.HeaderV2.Title>
        }
        subTitle={
          <Truncated color='subdued' as={Text} size='medium' css={{ maxWidth: '700px' }}>
            {subtitle}
          </Truncated>
        }
        action={
          <ChartEditPanel
            xAxisOptions={X_AXIS_TIME_FILTER_OPTIONS}
            yAxisGroups={yAxisGroups}
            defaultYAxisSelected={defaultYAxisSelected}
            defaultXAxisSelected={TimeFilterType.DAILY}
            onXChange={onXChange}
            onYChange={onYChange}
          />
        }
      />
      <Chart.BarChart
        data={chartData}
        appearance={{
          mode: 'stacked',
          barSize: 24,
          showXAxis: true,
          showYAxis: true,
          height: 240,
          barRadius: 4,
          hideMarkers: true,
          customTooltipTitle: ({ groupName }) =>
            tooltipHeadersByName[groupName] ?? tooltipTitlesByName[groupName] ?? groupName,
          customTooltipData,
        }}
        onClick={selectedXAxis === TimeFilterType.DAILY ? handleOnBarClick : undefined}
      />
      <Chart.Legends doNotCollapseLegends values={legendValues} />
    </Chart>
  );
};
